import { canv } from "@/canv/global.js";
import { getColorProperty, getNumberProperty, getStringProperty, isBrowser } from "@/canv/utils.js";
import { elementBase } from "@/elementBase/elementBase.js";
import type { textWidget } from "@/elements/text/text.js";
import {
    createTextPartByEntityType,
    ensureTextPartByEntityType,
    regenerateTextPartUID,
    resolveTextPartEntityTypeFromState,
} from "@/elements/text/textPartFactory";
import { TextWidgetV2 } from "@/elements/text/textV2";
import { generateKeyBetween } from "@/helpers/fractional-indexing.js";
import { getFontSize } from "@/utils.js";
import { widgetOption } from "@/widgetMenu/widgetMenu.js";
import type {
    BoxData,
    InteractionResult,
    TreeNodeWidgetFixReason,
    TreeNodeWidgetFixState,
    TreeNodeWidgetStateFull,
    TreeNodeWidgetStateMap,
} from "./types";

export class TreeNodeWidget extends elementBase {
    constructor() {
        super();
        this.width = this.minWidth;
        this.fastAction = "";

        this.textPart = createTextPartByEntityType(
            "textWidgetV2",
            this.startX + this.horizontalPadding,
            this.startY + this.topPadding,
        );
        this.setupTextPart();

        this.rotatable = false;

        this.disableConnectors = true;
        this.disableLevelLines = true;

        this.updateDims();
    }

    private setupTextPart() {
        this.textPart.setKeepMinWidth(false);
    }

    regenerateUIDS(): void {
        regenerateTextPartUID(this.textPart, this.UID);
    }

    updateDims() {
        if (!isBrowser()) return;

        const lineHeight =
            this.textPart instanceof TextWidgetV2
                ? this.textPart.lineHeight
                : this.textPart.lines[0].textHeight;

        this.textPart.startX = this.startX + this.horizontalPadding;
        this.textPart.startY =
            this.startY + this.height / 2 - this.textPart.height / 2 + lineHeight;

        this.textPart.updateDims();

        if (this.textPart.symbolsCount === 0) {
            this.width = this.minWidth;
        } else {
            this.width = 2 * this.horizontalPadding + this.textPart.width;
        }

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
        if (w < 2 * r) r = w / 2;
        if (h < 2 * r) r = h / 2;
        canv.ctx.beginPath();
        canv.ctx.moveTo(x + r, y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, r);
        canv.ctx.arcTo(x + w, y + h, x, y + h, r);
        canv.ctx.arcTo(x, y + h, x, y, r);
        canv.ctx.arcTo(x, y, x + w, y, r);
        canv.ctx.closePath();
    }

    shouldDraw() {
        if (this.parent.isGroupOnScreen()) return true;

        // если кто-то из child на экране, то рисуем, чтобы нарисовать связи
        for (let i = 0; i < this.children.length; i++) {
            const child = canv.canvasController.FindByUID(this.children[i].UID);
            if (child == null) return;

            if (child.parent.isGroupOnScreen()) {
                return true;
            }
        }
    }

    draw() {
        canv.ctx.lineWidth = 4;

        if (this.parentNode === "" && this.color !== "transparent") {
            canv.ctx.strokeStyle = this.color;
            this.drawRoundedRect(
                this.startX,
                this.startY,
                this.width,
                this.height,
                canv.actionManager.getConstant(50),
            );
            canv.ctx.stroke();
        }

        if (this.shouldHighlight()) {
            canv.ctx.strokeStyle = "#0d99ff";
            this.drawRoundedRect(
                this.startX,
                this.startY,
                this.width,
                this.height,
                canv.actionManager.getConstant(50),
            );
            canv.ctx.stroke();
        }

        canv.ctx.strokeStyle = this.color;

        // рисуем связи
        for (let i = 0; i < this.children.length; i++) {
            const widget = this.children[i];
            if (widget == null) continue;
            let dir = this.direction;

            if (this.direction === "") {
                dir = widget.direction;
            }

            const fixedGap = canv.actionManager.getConstant(50);
            if (dir === "right") {
                canv.ctx.beginPath();
                canv.ctx.moveTo(this.startX + this.width, this.startY + this.height / 2);
                canv.ctx.bezierCurveTo(
                    this.startX + this.width + fixedGap,
                    this.startY + this.height / 2,
                    widget.startX - fixedGap,
                    widget.startY + widget.height / 2,
                    widget.startX,
                    widget.startY + widget.height / 2,
                );
                canv.ctx.stroke();
                canv.ctx.closePath();
            } else {
                canv.ctx.beginPath();
                canv.ctx.moveTo(this.startX, this.startY + this.height / 2);
                canv.ctx.bezierCurveTo(
                    this.startX - fixedGap,
                    this.startY + this.height / 2,
                    widget.startX + widget.width + fixedGap,
                    widget.startY + widget.height / 2,
                    widget.startX + widget.width,
                    widget.startY + widget.height / 2,
                );
                canv.ctx.stroke();
                canv.ctx.closePath();
            }
        }

        // рисуем + при наведении
        const plusSide = this.shouldShowPlus();
        if (plusSide !== "") {
            const radius = canv.actionManager.getZoomConstant(15);
            const margin = radius;
            const memOpacity = canv.ctx.globalAlpha;

            let centerX = this.startX + this.width + margin + radius;
            const centerY = this.startY + this.height / 2;

            if (plusSide === "left") {
                centerX = this.startX - margin - radius;
            }

            canv.ctx.fillStyle = "#CCCCCC";

            if (this.isOverLeftPlus() || this.isOverRightPlus()) {
                canv.ctx.fillStyle = "blue";
            }

            canv.ctx.globalAlpha = 0.5;
            canv.ctx.beginPath();
            canv.ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            canv.ctx.fill();

            canv.ctx.fillStyle = "white";
            canv.ctx.globalAlpha = memOpacity;

            const plusWidth = radius * 0.2;
            const plusHeight = radius * 1.2;
            canv.ctx.fillRect(
                centerX - plusWidth / 2,
                this.startY + this.height / 2 - plusHeight / 2,
                plusWidth,
                plusHeight,
            );
            canv.ctx.fillRect(
                centerX - plusHeight / 2,
                this.startY + this.height / 2 - plusWidth / 2,
                plusHeight,
                plusWidth,
            );
        }

        this.textPart.draw();
    }

    TryAction(type: string) {
        if (type === "keyboardDown" && canv.keyboard.isEnter()) {
            if (this.parentNode === "") return;

            this.reset();
            const parent = canv.canvasController.FindByUID(this.parentNode) as TreeNodeWidget;
            parent?.addChild(this.direction);
            return;
        }

        this.textPart.TryAction(type);
        this.updateDims();
        if (type === "mousedown" && !this.textPart.isOver()) {
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    becomingFocused() {
        super.becomingFocused();
        this.fixState("full");
        this.textPart.becomingFocused();
    }

    becomingSelected() {
        super.becomingSelected();
        this.textPart.becomingSelected();
    }

    reset() {
        if (this.isFocused) {
            canv.eventManager.openRecord();
            this.parent.saveWidgets("full");
            canv.eventManager.closeRecord();
        }
        super.reset();
        this.textPart.reset();
    }

    beforeDeletion() {
        /*
            Нужно чтобы удаляемая нода ни на кого не ссылалась и на нее никто не ссылался
        */

        this.runTopSelectedFromRoot();

        if (this.UID === this.topSelected && this.parentNode !== "") {
            canv.eventManager.createTouchEvent(this.parentNode);
        }

        // отвязываем ноду, чтобы delete -> undo -> redo (нода будет удаляться средствами build event и не будет отвязывать автоматически)
        this.fixState("parentNode");
        this.registerNodeParent("");
        this.saveState("parentNode");

        const childs: TreeNodeWidget[] = [];
        childs.splice(0, 0, ...this.children);

        for (let i = 0; i < childs.length; i++) {
            // отцепляем детей
            childs[i].fixState("parentNode");
            childs[i].registerNodeParent("");
            childs[i].saveState("parentNode");
            if (!canv.canvasController.isInSelectedDeep(childs[i].parent)) {
                // deep на всякий, нода не должна оказаться в группе
                canv.canvasController.deleteProcessor(childs[i].parent);
            }
        }
    }

    runTopSelectedFromRoot() {
        if (this.parentNode === "") {
            this.setTopSelected("");
        } else {
            const parentWidget = canv.canvasController.FindByUID(this.parentNode) as TreeNodeWidget;
            if (parentWidget != null) {
                // такое получается, если удалить две ноды. Первое удаление прерывает цепочку. Но topSelected уже у всех есть
                parentWidget.runTopSelectedFromRoot();
            }
        }
    }

    setTopSelected(topSelected: string) {
        if (topSelected === "") {
            if (canv.canvasController.isInSelectedDeep(this.parent)) {
                topSelected = this.UID;
            }
        }

        this.topSelected = topSelected;

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].setTopSelected(topSelected);
        }
    }

    shouldShowPlus() {
        let plusSide = "";

        const radius = canv.actionManager.getZoomConstant(15); // радиус плюса

        const pt = canv.actionManager.getActionPoint();

        if (
            this.direction !== "left" &&
            pt.x > this.startX + this.width / 2 &&
            pt.x < this.startX + this.width + 3 * radius
        ) {
            if (pt.y > this.startY && pt.y < this.startY + this.height) {
                plusSide = "right";
            }
        }

        if (
            this.direction !== "right" &&
            pt.x > this.startX - 3 * radius &&
            pt.x < this.startX + this.width / 2
        ) {
            if (pt.y > this.startY && pt.y < this.startY + this.height) {
                plusSide = "left";
            }
        }

        return plusSide;
    }

    isOverRightPlus() {
        if (this.direction === "left") {
            return false;
        }

        const radius = canv.actionManager.getZoomConstant(15); // радиус плюса

        const pt = canv.actionManager.getActionPoint();

        if (
            pt.x > this.startX + this.width + radius &&
            pt.x < this.startX + this.width + 3 * radius
        ) {
            if (
                pt.y > this.startY + this.height / 2 - radius &&
                pt.y < this.startY + this.height / 2 + radius
            ) {
                return true;
            }
        }

        return false;
    }

    isOverLeftPlus() {
        if (this.direction === "right") {
            return false;
        }

        const radius = canv.actionManager.getZoomConstant(15); // радиус плюса

        const pt = canv.actionManager.getActionPoint();

        if (pt.x > this.startX - 3 * radius && pt.x < this.startX - radius) {
            if (
                pt.y > this.startY + this.height / 2 - radius &&
                pt.y < this.startY + this.height / 2 + radius
            ) {
                return true;
            }
        }

        return false;
    }

    isMovingNode() {
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        if (cursorTool.mode === "dragging" || cursorTool.mode === "fastDragging") {
            if (
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0].isItemWrapper() &&
                canv.canvasController.selected[0].onlyChild()?.constructorName === "treeNode"
            ) {
                return true;
            }
        }

        return false;
    }

    shouldHighlight() {
        if (!this.isMovingNode()) return false;

        const movingNode = canv.canvasController.selected[0].onlyChild();
        const movingNodeEqualThis = movingNode?.UID === this.UID;

        const pt = canv.actionManager.getActionPoint();

        if (
            !movingNodeEqualThis &&
            pt.x >= this.startX &&
            pt.x <= this.startX + this.width &&
            pt.y >= this.startY &&
            pt.y <= this.startY + this.height
        ) {
            return true;
        }
    }

    preparePaste() {
        this.parentNode = "";
        this.direction = "";
    }

    // меняет направление ветки, кидает delta
    setDirection(newDirection: string, throwEvents = false) {
        // при вызове из TryChangeDirection throwEvents == false
        // мы не кидаем event потому, что setDirection влияет только на startX, startY, direction передвигаемой ноды и ее children
        // все эти поля мы сохраним в translatingStop->saveStateRecursively

        if (throwEvents && this.direction !== newDirection) {
            this.fixState("move");
            this.direction = newDirection;
            this.saveState();
        } else {
            this.direction = newDirection;
        }

        for (let i = 0; i < this.children.length; i++) {
            const childNode = this.children[i];
            if (childNode == null) return;

            if (childNode.direction === "right" && newDirection === "left") {
                const gap = childNode.startX - this.startX - this.width;
                const newStartX = this.startX - gap - childNode.width;
                if (throwEvents) {
                    childNode.translateInnerAndEvent(newStartX - childNode.startX, 0);
                } else {
                    childNode.translateInner(newStartX - childNode.startX, 0);
                }
            } else if (childNode.direction === "left" && newDirection === "right") {
                const gap = this.startX - childNode.startX - childNode.width;
                const newStartX = this.startX + this.width + gap;
                if (throwEvents) {
                    childNode.translateInnerAndEvent(newStartX - childNode.startX, 0);
                } else {
                    childNode.translateInner(newStartX - childNode.startX, 0);
                }
            }
            childNode.setDirection(newDirection, throwEvents);
        }
    }

    // двигает ноды и кидает delta, вызывается из widget menu
    alignChilds(direction: string) {
        const childsSet: TreeNodeWidget[] = [];

        for (let i = 0; i < this.children.length; i++) {
            const childNode = this.children[i];
            if (childNode.direction !== direction) continue;

            childsSet.push(childNode);
        }

        let accumHeight = 0;
        const gap = this.height;
        for (let i = 0; i < childsSet.length; i++) {
            const childNode = childsSet[i];
            childNode.alignChilds(direction);

            const box = childNode.getBox();

            accumHeight += box.max - box.min;

            if (i !== childsSet.length - 1) {
                accumHeight += gap;
            }
        }

        let topStartY = this.startY + this.height / 2 - accumHeight / 2;

        for (let i = 0; i < childsSet.length; i++) {
            const childNode = childsSet[i];

            const box = childNode.getBox();
            const boxHeight = box.max - box.min;

            let minorDistanceMove =
                this.startX + this.width - childNode.startX + canv.actionManager.getConstant(150);
            if (direction === "left") {
                minorDistanceMove =
                    this.startX -
                    childNode.startX -
                    childNode.width -
                    canv.actionManager.getConstant(150);
            }
            childNode.translateInnerAndEvent(minorDistanceMove, topStartY - box.min);
            topStartY = topStartY + boxHeight + gap;
        }
    }

    // кидает delta
    registerChild(childUID: string): void {
        this.fixState("structure");
        // children массив из treeNodeWidget, а UID - строка, указано any для сохранения логики
        this.children.push(childUID as any);
        this.saveState();

        const child = canv.canvasController.FindByUID(childUID) as TreeNodeWidget;
        if (child == null) return;

        child.fixState("structure");
        child.parentNode = this.UID;
        child.saveState();
    }

    registerNodeParent(parentWidgetUID = "") {
        if (this.parentNode !== "") {
            const currentParentWidget = canv.canvasController.FindByUID(
                this.parentNode,
            ) as TreeNodeWidget;
            if (currentParentWidget != null) {
                for (let i = 0; i < currentParentWidget.children.length; i++) {
                    if (currentParentWidget.children[i].UID === this.UID) {
                        currentParentWidget.children.splice(i, 1);
                    }
                }
            }
        }

        this.parentNode = parentWidgetUID;
        const parentWidget = canv.canvasController.FindByUID(parentWidgetUID) as TreeNodeWidget;
        if (parentWidget != null) {
            parentWidget.children.push(this);
        }
    }

    isNeedInit() {
        return true;
    }

    initWidget() {
        if (this.parentNode === "") return;

        this.registerNodeParent(this.parentNode);
    }

    checkDeletion() {
        return this.children.length === 0;
    }

    translatingStart() {
        this.fixStateRecursively("move");
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        for (let i = 0; i < this.children.length; i++) {
            const widget = this.children[i];
            widget.translateInner(x, y, true);
        }

        this.updateDims();
        this.TryChangeDirection();
    }

    translateInner(x: number, y: number, ignoreSelected = false) {
        // везде в коде вызывается просто translateInner(x, y)
        // translateInner(x, y, true) вызывается только из translate, чтобы children не двигались если выбраны
        if (ignoreSelected) {
            // не двигаемся если нас двигал родитель, а мы есть в selected
            if (canv.canvasController.isInSelectedDeep(this.parent)) {
                return;
            }
        }

        this.startX += x;
        this.startY += y;
        for (let i = 0; i < this.children.length; i++) {
            const widget = this.children[i];
            widget.translateInner(x, y, ignoreSelected);
        }
        this.updateDims();
    }

    // вызывает setDirection по предикату
    TryChangeDirection() {
        // вызывается из translate, setDirection не кидает event кода двигает ноды, потому что они все будут позже saveState через saveStateRecursively в translatingStop
        // То-есть saveDirection тут просто дополнительно двигает child nodes
        if (this.parentNode !== "") {
            const parent = canv.canvasController.FindByUID(this.parentNode) as TreeNodeWidget;
            const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
            const movingMode = cursorTool.mode === "dragging" || cursorTool.mode === "fastDragging";
            const movingOnlyTreeNode =
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0].isItemWrapper() &&
                canv.canvasController.selected[0].onlyChild()?.constructorName === "treeNode";

            if (parent && parent.parentNode === "" && movingMode && movingOnlyTreeNode) {
                const pt = canv.actionManager.getActionPoint();
                if (pt.x <= parent.startX + parent.width / 2 && this.direction === "right") {
                    // меняем direction у root child
                    this.setDirection("left");
                } else if (pt.x > parent.startX + parent.width / 2 && this.direction === "left") {
                    // меняем direction у root child
                    this.setDirection("right");
                }
            }
        }
    }

    // делает translateInner и сам кидает delta
    translateInnerAndEvent(x: number, y: number) {
        this.fixStateRecursively("move");
        this.translateInner(x, y);
        this.saveStateRecursively();
    }

    translatingStop() {
        this.saveStateRecursively();
    }

    translatingAfter() {
        // тут мы уже все подвинули и сохранили. Делаем пост модерирование.

        if (this.isMovingNode() && this.parentNode !== "") {
            const items = canv.canvasController.getVisibleItemWrappersExceptSelected();
            for (let i = 0; i < items.length; i++) {
                const parentCandidate = items[i].onlyChild() as TreeNodeWidget;
                if (!parentCandidate) continue;

                if (parentCandidate.constructorName !== "treeNode") continue;
                if (parentCandidate.UID === this.parentNode) continue;

                const pt = canv.actionManager.getActionPoint();

                if (
                    pt.x >= parentCandidate.startX &&
                    pt.x <= parentCandidate.startX + parentCandidate.width &&
                    pt.y >= parentCandidate.startY &&
                    pt.y <= parentCandidate.startY + parentCandidate.height
                ) {
                    canv.eventManager.createTouchEvent(parentCandidate.UID);

                    this.fixState("parentNode");
                    this.registerNodeParent(parentCandidate.UID);
                    this.saveState();

                    let newDirection = parentCandidate.direction;
                    if (newDirection === "") {
                        newDirection = "right";
                    }
                    this.setDirection(newDirection, true);

                    let newX =
                        parentCandidate.startX +
                        parentCandidate.width +
                        canv.actionManager.getConstant(150);
                    if (newDirection === "left") {
                        newX =
                            parentCandidate.startX -
                            parentCandidate.width -
                            canv.actionManager.getConstant(150);
                    }

                    const newY = parentCandidate.startY;
                    this.translateInnerAndEvent(newX - this.startX, newY - this.startY);

                    parentCandidate.alignAroundAndUp(this.UID, this.getBox());

                    break;
                }
            }
        }

        // alignAroundAndUp умеет двигать произвольные ноды, но сохраняет delta
        if (this.parentNode !== "") {
            const parent = canv.canvasController.FindByUID(this.parentNode) as TreeNodeWidget;
            const boxData = this.getBox();
            parent.alignAroundAndUp(this.UID, boxData);
        }
    }

    fixStateRecursively(reason: string) {
        this.fixState(reason);

        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            child.fixStateRecursively(reason);
        }
    }

    saveStateRecursively() {
        this.saveState();

        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            child.saveStateRecursively();
        }
    }

    resize() {}

    // isInteraction, вызывает addChild

    isInteraction(): InteractionResult {
        if (this.isOverRightPlus()) {
            return { type: "click" };
        }

        if (this.isOverLeftPlus()) {
            return { type: "click" };
        }

        return { type: "none" };
    }

    isInteractionClick() {
        if (this.isOverRightPlus()) {
            this.addChild("right");
        }

        if (this.isOverLeftPlus()) {
            this.addChild("left");
        }
    }

    // создает child and throw build, delta
    addChild(direction) {
        const mmTool = canv.toolBox.TryGetToolByName("mindMapTool");
        let newX = this.startX + this.width + canv.actionManager.getConstant(150);
        if (direction === "left") {
            const afterCreationWidth = this.minWidth + 2 * this.horizontalPadding;
            newX = this.startX - afterCreationWidth - canv.actionManager.getConstant(150);
        }
        let newY = this.startY;
        if (this.children.length != 0) {
            // высота у всех одинаковая
            let bottomEdge: number | undefined;
            for (let i = 0; i < this.children.length; i++) {
                const widget = this.children[i];
                if (widget.direction !== direction) continue;
                if (bottomEdge === undefined || widget.startY + widget.height > bottomEdge) {
                    bottomEdge = widget.startY + widget.height;
                }
            }

            if (bottomEdge !== undefined) {
                newY = bottomEdge;
            }
        }

        // createNode больше не поддерживается в mindMapTool
        // const newChild = mmTool.createNode("", newX, newY, direction, this.textPart.fontSize, this.textPart.fontBold, this.height);
        //
        //
        // canv.canvasController.addGroup(newChild);
        //
        // canv.eventManager.openRecord();
        // canv.eventManager.createTouchEvent(this.UID);
        // canv.eventManager.createBuildEvent("create", newChild);
        //
        // newChild.onlyChild().fixState("parentNode");
        // newChild.onlyChild().registerNodeParent(this.UID);
        // newChild.onlyChild().saveState();
        //
        // // выкидывает delta
        // this.alignAroundAndUp(newChild.onlyChild().UID, newChild.onlyChild().getBox());
        //
        // canv.eventManager.closeRecord();

        // canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(newChild);
    }

    // выравнивание соседей

    getBox(): BoxData {
        const boxData: BoxData = {
            min: this.startY,
            max: this.startY + this.height,
        };

        for (let i = 0; i < this.children.length; i++) {
            const widget = this.children[i];
            const res = widget.getBox();
            if (res.min < boxData.min) {
                boxData.min = res.min;
            }

            if (res.max > boxData.max) {
                boxData.max = res.max;
            }
        }

        return boxData;
    }

    // вызывается только из alignAroundAndUp, двигает соседей выбрасывает для них delta
    repositionAround(aroundUID: string, boxData: BoxData): void {
        if (this.children.length === 0) return;

        if (this.children.length === 0) return;

        const compare = (widgetA, widgetB) => {
            if (widgetA.startY < widgetB.startY) {
                return -1;
            }
            if (widgetA.startY > widgetB.startY) {
                return 1;
            }
            return 0;
        };

        this.children.sort(compare);
        let aroundIndex = 0;

        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i].UID === aroundUID) {
                aroundIndex = i;
            }
        }

        const aroundWidget = this.children[aroundIndex];

        let border = boxData.min;
        const margin = this.height; // такое значение нужно, чтобы когда например root и два сына, между ними была одна высота и все выглядело выравнено
        for (let i = aroundIndex - 1; i >= 0; i--) {
            const widget = this.children[i];

            // чтобы при движении нод справа не выравнивать ноды слева
            // актуально только для root
            if (widget.direction !== aroundWidget.direction) continue;

            const currentBox = widget.getBox();
            let newBorder = currentBox.min;
            if (currentBox.max + margin > border) {
                widget.translateInnerAndEvent(0, border - currentBox.max - margin);
                newBorder = newBorder + (border - currentBox.max - margin);
            }
            border = newBorder;
        }

        border = boxData.max;
        for (let i = aroundIndex + 1; i < this.children.length; i++) {
            const widget = this.children[i];

            // чтобы при движении нод справа не выравнивать ноды слева
            // актуально только для root
            if (widget.direction !== aroundWidget.direction) continue;

            const currentBox = widget.getBox();
            let newBorder = currentBox.max;
            if (currentBox.min - margin < border) {
                widget.translateInnerAndEvent(0, border - currentBox.min + margin);
                newBorder += border - currentBox.min + margin;
            }
            border = newBorder;
        }
    }

    // вызывает repositionAround и alignAroundAndUp для parent
    alignAroundAndUp(aroundUID: string, boxData: BoxData) {
        this.repositionAround(aroundUID, boxData);
        const updatedBoxData = this.getBox();
        if (this.parentNode !== "") {
            const parent = canv.canvasController.FindByUID(this.parentNode) as TreeNodeWidget;
            parent.alignAroundAndUp(this.UID, updatedBoxData);
        }
    }

    menuColor(color: string) {
        this.color = color;
    }

    alignChildsMenu() {
        this.alignChilds("right");
        this.alignChilds("left");
    }

    menuUpdateTextParamsChilds(
        size: number,
        boldness: string,
        height: number,
        horizontalPadding: number,
    ): void {
        this.fixState("full");
        if (this.textPart instanceof TextWidgetV2) {
            this.textPart.menuTextSize(size);
            this.textPart.menuTextBold(boldness === "bold");
        } else {
            this.textPart.lines[0].setTextSize(size, 0, this.textPart.lines[0].items.length);
            this.textPart.lines[0].setTextBold(boldness, 0, this.textPart.lines[0].items.length);
            this.textPart.fontSize = size;
            this.textPart.fontBold = boldness;
        }
        this.textPart.updateDims();
        this.height = height;
        this.horizontalPadding = horizontalPadding;
        this.saveState();

        this.updateDims();

        for (let i = 0; i < this.children.length; i++) {
            const childNode = this.children[i];
            childNode.menuUpdateTextParamsChilds(size, boldness, height, horizontalPadding);
        }
    }

    menuUpdateTextParams(size: number, boldness: string) {
        const oldTextHeight = this.textPart.height;
        if (this.textPart instanceof TextWidgetV2) {
            this.textPart.menuTextSize(size);
            this.textPart.menuTextBold(boldness === "bold");
        } else {
            this.textPart.lines[0].setTextSize(size, 0, this.textPart.lines[0].items.length);
            this.textPart.lines[0].setTextBold(boldness, 0, this.textPart.lines[0].items.length);
            this.textPart.fontSize = size;
            this.textPart.fontBold = boldness;
        }
        this.textPart.updateDims();
        const newTextHeight = this.textPart.height;

        const ratio = newTextHeight / oldTextHeight;
        this.height *= ratio;
        this.horizontalPadding *= ratio;

        this.updateDims();

        for (let i = 0; i < this.children.length; i++) {
            const childNode = this.children[i];
            childNode.menuUpdateTextParamsChilds(
                size,
                boldness,
                this.height,
                this.horizontalPadding,
            );
        }

        // выравнивает и кидает delta
        this.alignChildsMenu();
    }

    menuTextSize(size: number) {
        const newSize = getFontSize(size);
        const boldness =
            this.textPart instanceof TextWidgetV2
                ? this.textPart.fontBold
                    ? "bold"
                    : "normal"
                : this.textPart.fontBold;
        this.menuUpdateTextParams(newSize, boldness);
    }

    menuTextBold(boldness: string) {
        this.menuUpdateTextParams(this.textPart.fontSize, boldness);
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];

        widgetOptions.push(new widgetOption("color", this.color, this.UID));
        widgetOptions.push(new widgetOption("alignChilds", "", this.UID));

        if (this.parentNode === "") {
            widgetOptions.push(new widgetOption("textSize", this.textPart.fontSize, this.UID));
            const boldValue =
                this.textPart instanceof TextWidgetV2
                    ? this.textPart.fontBold
                        ? "bold"
                        : "normal"
                    : this.textPart.fontBold;
            widgetOptions.push(new widgetOption("textBold", boldValue, this.UID));
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        } else if (name === "alignChilds") {
            return this.alignChildsMenu;
        } else if (name === "textSize") {
            return this.menuTextSize;
        } else if (name === "textBold") {
            return this.menuTextBold;
        }
    }

    // Далее для statesManager

    collectChildren(collection: TreeNodeWidgetStateFull[]) {
        const compare = (widgetA: TreeNodeWidget, widgetB: TreeNodeWidget) => {
            if (widgetA.startY < widgetB.startY) {
                return -1;
            }
            if (widgetA.startY > widgetB.startY) {
                return 1;
            }
            return 0;
        };

        let lastLevel: string | null = null;

        this.children.sort(compare);
        for (let i = 0; i < this.children.length; i++) {
            const state = this.children[i].getState("full");
            state.level = generateKeyBetween(lastLevel, null);
            lastLevel = state.level;
            collection.push(state);
        }

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].collectChildren(collection);
        }
    }

    getFixType(reason: TreeNodeWidgetFixReason): TreeNodeWidgetFixState {
        let fixType: TreeNodeWidgetFixState = "full";

        switch (reason) {
            case "wm_color":
                fixType = "color";
                break;
            case "move":
                fixType = "move";
                break;
            case "parentNode":
                fixType = "parentNode";
        }

        return fixType;
    }

    getState<T extends TreeNodeWidgetFixState>(fixType: T): TreeNodeWidgetStateMap[T] {
        switch (fixType) {
            case "color":
                return {
                    color: this.color,
                } as TreeNodeWidgetStateMap[T];
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    direction: this.direction,
                } as TreeNodeWidgetStateMap[T];
            case "parentNode":
                return {
                    parentNode: this.parentNode,
                } as TreeNodeWidgetStateMap[T];
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "treeNodeWidget",
                    parentNode: this.parentNode,
                    startX: this.startX,
                    startY: this.startY,
                    textPart: this.textPart.getState("full"),
                    height: this.height,
                    horizontalPadding: this.horizontalPadding,
                    color: this.color,
                    direction: this.direction,
                } as TreeNodeWidgetStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends TreeNodeWidgetFixState>(stateObj: TreeNodeWidgetStateMap[T], fixType: T) {
        switch (fixType) {
            case "color": {
                this.color = getColorProperty("color", stateObj);
                break;
            }
            case "move": {
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.direction = getStringProperty("direction", stateObj);
                break;
            }
            case "parentNode": {
                this.registerNodeParent(getStringProperty("parentNode", stateObj));
                break;
            }
            case "full": {
                const fullState = stateObj as TreeNodeWidgetStateMap["full"];
                this.UID = getStringProperty("UID", fullState);
                this.parentUID = getStringProperty("parentUID", fullState);
                this.startX = getNumberProperty("startX", fullState);
                this.startY = getNumberProperty("startY", fullState);
                this.parentNode = getStringProperty("parentNode", fullState);
                const textPartType = resolveTextPartEntityTypeFromState(fullState.textPart);
                this.textPart = ensureTextPartByEntityType(
                    this.textPart,
                    textPartType,
                    this.startX + this.horizontalPadding,
                    this.startY + this.topPadding,
                );
                this.setupTextPart();
                this.textPart.loadState(fullState.textPart, "full");
                this.height = getNumberProperty("height", fullState);
                this.horizontalPadding = getNumberProperty("horizontalPadding", fullState);
                this.direction = getStringProperty("direction", fullState);
                this.color = getColorProperty("color", fullState);
                break;
            }
        }

        this.updateDims();
    }

    constructorName = "treeNode";
    width = 0; // >= minWidth. Рассчитывается в updateDims
    textPart: textWidget | TextWidgetV2;
    children: TreeNodeWidget[] = []; // widgets of treeNode
    topSelected = "";

    // Константы
    minWidth = 250;
    horizontalPadding = 30;
    topPadding = 0;
    fontSize = 25;

    // Сохраняемые данные
    startX = 0;
    startY = 0;
    height = 0;
    direction = "right";
    color = "#6F38F5";
    parentNode = ""; // parent node UID
    isFocused = false;
}
